import { useEffect, useRef, useState } from "react";
import { animate, stagger } from "animejs";
import {
  Mail,
  Phone,
  MapPin,
  Copy,
  Check,
  ExternalLink,
} from "lucide-react";
import { GithubIcon, LinkedinIcon } from "../../components/ui/brand-icons";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

const CHANNELS = [
  {
    label: "Email",
    value: "arnupap.sukserm@gmail.com",
    href: "mailto:arnupap.sukserm@gmail.com",
    icon: Mail,
    accent: "from-cyan-500/30 to-cyan-500/0",
  },
  {
    label: "GitHub",
    value: "https://github.com",
    href: "https://github.com/comeonlnw",
    icon: GithubIcon,
    accent: "from-fuchsia-500/30 to-fuchsia-500/0",
  },
  {
    label: "LinkedIn",
    value: "linkedin.com",
    href: "https://www.linkedin.com/in/arnupap-sukserm-0b2a893b2/",
    icon: LinkedinIcon,
    accent: "from-violet-500/30 to-violet-500/0",
  },
  {
    label: "Phone",
    value: "+66 (0) 9X XXX XXXX",
    href: "tel:+66000000000",
    icon: Phone,
    accent: "from-emerald-500/30 to-emerald-500/0",
  },
];

const ChannelCard = ({ ch }) => {
  const [copied, setCopied] = useState(false);
  const Icon = ch.icon;

  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(ch.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  return (
    <div className="channel-card">
      <Card className="group relative overflow-hidden p-5 transition-all hover:-translate-y-1 hover:border-cyan-400/30 hover:shadow-[0_0_40px_-10px_rgba(34,211,238,0.5)]">
        <div
          className={`pointer-events-none absolute -inset-x-10 -top-20 h-40 bg-gradient-to-b ${ch.accent} blur-2xl opacity-40 transition-opacity group-hover:opacity-90`}
        />
        <div className="relative flex items-start gap-4">
          <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-white/10 bg-white/5 text-cyan-300">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              {ch.label}
            </p>
            <p className="truncate text-base font-semibold">{ch.value}</p>
          </div>
        </div>
        <div className="relative mt-5 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => window.open(ch.href, "_blank", "noopener")}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCopy}
            className="border border-white/10"
          >
            {copied ? (
              <>
                <Check className="h-3.5 w-3.5 text-emerald-400" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3.5 w-3.5" />
                Copy
              </>
            )}
          </Button>
        </div>
      </Card>
    </div>
  );
};

const ContactsIndex = () => {
  const heroRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    animate(el.querySelectorAll("[data-char]"), {
      opacity: [0, 1],
      translateY: [30, 0],
      filter: ["blur(6px)", "blur(0px)"],
      duration: 700,
      delay: stagger(40),
      ease: "outExpo",
    });
  }, []);


  const title = "Let's talk.";

  return (
    <section
      ref={heroRef}
      className="mx-auto flex min-h-screen max-w-6xl flex-col items-center justify-center px-6 py-24"
    >
      <div className="rise-in mb-6" style={{ animationDelay: "100ms" }}>
        <Badge variant="neon" className="gap-2">
          <MapPin className="h-3 w-3" /> Bangkok, Thailand · GMT+7
        </Badge>
      </div>

      <h1
        ref={titleRef}
        className="text-glow text-center text-5xl font-bold leading-tight sm:text-6xl lg:text-7xl"
      >
        {title.split("").map((c, i) => (
          <span
            key={i}
            data-char
            className="inline-block opacity-0"
            style={{ whiteSpace: c === " " ? "pre" : "normal" }}
          >
            {c}
          </span>
        ))}
      </h1>

      <p
        className="rise-in mt-6 max-w-xl text-center text-base text-white/60 lg:text-lg"
        style={{ animationDelay: "1100ms" }}
      >
        Got a project, idea or role you'd like to discuss? Pick the channel that
        works for you — I usually respond within 24h.
      </p>

      <div className="mt-12 grid w-full grid-cols-1 gap-5 sm:grid-cols-2">
        {CHANNELS.map((ch, i) => (
          <div
            key={ch.label}
            className="rise-in"
            style={{ animationDelay: `${1300 + i * 120}ms` }}
          >
            <ChannelCard ch={ch} />
          </div>
        ))}
      </div>

      <div className="rise-in mt-12" style={{ animationDelay: "2000ms" }}>
        <Button
          variant="neon"
          size="xl"
          onClick={() =>
            (window.location.href = "mailto:arnupap.sukserm@gmail.com")
          }
        >
          <Mail className="h-5 w-5" />
          Send me an email
        </Button>
      </div>
    </section>
  );
};

export default ContactsIndex;
